/*
 *  Copyright (c) Qwoach LLC 2023. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 4/11/2023
 */
import {Type} from "@qwoach/class-transformer-wrapper";
import {FilterableProperty} from "@shared/Filterable/PropertyDecorators.js";
import AddressItem from "@shared/Profile/AddressItem.js";
import TimeZoneItem from "@shared/Profile/TimeZoneItem.js";
import {InviteStatus} from "@shared/Profile/InviteStatus.js";
import TagItem from "@shared/Tags/TagItem.js";
import ProfileShort from "@shared/Profile/ProfileShort.js";

import ParticipantItem from "@shared/scheduler/ParticipantItem.js";
import AccessRuleItem from "@shared/Profile/AccessRuleItem.js";
import {findProfileTypeLabelByValue, ProfileTypeEnum} from "@shared/Profile/ProfileTypeEnum.js";

import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import QwoachFile from "@shared/Drive/QwoachFile.js";
import FilterableItem from "@shared/Filterable/FilterableItem.js";
import {isObject, logError, stringContainsAny} from "@/utils/helpers";
import {StatusEnum} from "@/core/components/Status/StatusEnum";
dayjs.extend(utc)
dayjs.extend(timezone)

let publicIdFormattedFn = (id) => `#${id.substring(0, 6)}`

export default class BaseProfile extends FilterableItem{

	@FilterableProperty({transform: id => publicIdFormattedFn(id), includeFullWord: true})
	id: string = null
	// @FilterableProperty()
	firstName: ?string = null
	// @FilterableProperty()
	lastName: ?string = null
	lastName_casefolded: String = null
	@FilterableProperty()
	name: ?string = null
	name_casefolded: string = null //needed to support case insensitive sorting. ref:https://stackoverflow.com/a/48098215/1713920

	@Type(() => QwoachFile)
	userPicture: QwoachFile = null

	@FilterableProperty()
	email: ?string = null
	@FilterableProperty()
	phone: ?string = null

	secondaryEmails: string[] = []

	@Type(() => AddressItem)
	@FilterableProperty(address => address.toString())
	primaryAddress: AddressItem = null

	profileImageUrl: string = null

	@Type(() => TimeZoneItem)
	timeZoneItem: TimeZoneItem = null
	dateOfBirth: number = null

	status: StatusEnum = StatusEnum.ACTIVE //ACTIVE, DISABLED, PENDING, ARCHIVED
	isEnabled: boolean = true //legacy field, use status instead

	@FilterableProperty(tag => tag.title)
	@Type(() => TagItem)
	tagItemsArray: TagItem[] = [];

	@Type(() => AccessRuleItem)
	accessRuleItem: AccessRuleItem = new AccessRuleItem() //Only writable by account owner; syncs back to userprofile custom claims

	portalInviteStatus: InviteStatus = InviteStatus.NOT_SET

	userProfileUid: string = null; //auth id of corresponding UserProfile

	bio: string = null

	@Type(() => ProfileShort)
	assignedTo: ProfileShort[] = []

	_notSaved: boolean = false

	profileType: ProfileTypeEnum = null


	constructor(options = {}) {
		super();
		if(!isObject(options)){
			options = {
				name: arguments[0] || null,
				email: arguments[1] || null,
				profileType: arguments[2] || null,
			}
		}
		console.log("BaseProfile.constructor: options = ", options, ", this = ", this)
		this.regenerateId()//we need this call here since BaseProfile defines id in order to apply filterable decorator, but that sets the id defined in super class to null. So we call regenerate from here.
		this.profileType = options.profileType
		options.name = options.name || ""
		options.email = options.email || ""
		this.setName(options.name);
		this.email = (options.email || "").toLowerCase().trim();
		this.syncRuleItemMyIdForOwner()
	}

	setId(id){
		super.setId(id)
		this.syncRuleItemMyIdForOwner()
		return this
	}


	regenerateId(){
		super.regenerateId()
		if(this.accessRuleItem){
			this.syncRuleItemMyIdForOwner()
		}
	}

	syncRuleItemMyIdForOwner(){
		this.accessRuleItem.myIdForOwner = this.id
	}

	getCurrentSchemaVersion() {
		return super.getCurrentSchemaVersion()
	}

	addRoles(roles){
		this.accessRuleItem.addRoles(roles)
	}
	setRoles(roles){
		this.accessRuleItem.setRoles(roles)
	}
	setScopes(scopes){
		this.accessRuleItem.setScopes(scopes)
	}
	setAccessLevel(accessLevel){
		this.accessRuleItem.setAccessLevel(accessLevel)
	}
	setAccessRules(roles, scopes, accessLevel){
		this.setRoles(roles)
		this.setScopes(scopes)
		this.setAccessLevel(accessLevel)
	}
	hasPermissionClientScopeAll(){
		return this.accessRuleItem.hasPermissionClientScopeAll()
	}

	addAssignedTo(baseProfile){
		let isAdded = this.assignedTo.pushUnique(new ProfileShort(baseProfile))
		if(isAdded){
			this.addSharedWithById(baseProfile.id)
		}
		return isAdded
	}
	removeAssignedTo(baseProfile){
		let isRemoved = this.assignedTo.deleteItem(new ProfileShort(baseProfile))
		if(isRemoved){
			this.removeSharedWithById(baseProfile.id)
		}
		return isRemoved
	}

	getPublicIdFormatted(){
		return publicIdFormattedFn(this.id)
	}

	isAssignedTo(baseProfile){
		if(typeof baseProfile === "string"){
			baseProfile = {id: baseProfile}
		}
		let res = this.assignedTo.find((item) => item.id === baseProfile.id)
		return res != null
	}

	getProfileTitle(){
		return findProfileTypeLabelByValue(this.profileType)
	}
	isClientProfile(){
		return this.profileType === ProfileTypeEnum.CLIENT
	}
	isTeamMember(){
		return this.profileType === ProfileTypeEnum.TEAM_MEMBER
	}

	getRolesFormatted(){
		return this.accessRuleItem.getRolesFormatted()
	}

	getSharedIdsObject(){
		return {
			clientId: this.id,
		}
	}

	getSharedIdsArray(){
		return [this.id]
	}


	setTimeZone(timezone){
		if(typeof timezone === "string"){
			this.timeZoneItem = new TimeZoneItem(timezone)
		} else {
			this.timeZoneItem = timezone
		}
	}

	addTags(tags){
		if(Array.isArray(tags)){
			this.tagItemsArray.push(...tags)
		} else {
			console.warn("ClientProfile.addTags: argument must be an array. An actual passed value was: ", tags)
		}
	}

	getAccessRule(){
		return this.accessRuleItem
	}
	getRoles(){
		return this.getAccessRule().getRoles()
	}

	isCoach(){
		return this.getAccessRule().isCoach()
	}
	isClient(){
		return this.getAccessRule().isClient()
	}
	isAdmin(){
		return this.getAccessRule().isAdmin()
	}

	fillFromAdditionalInfo(additionalInfoItem){
		let isChanged = false

		if(additionalInfoItem.name){
			this.name = additionalInfoItem.name
			isChanged = true
		}
		if(additionalInfoItem.email){
			this.email = additionalInfoItem.email
			isChanged = true
		}
		if(additionalInfoItem.company){
			this.company = additionalInfoItem.company
			isChanged = true
		}
		if(additionalInfoItem.primaryAddress){
			this.primaryAddress = additionalInfoItem.primaryAddress
			isChanged = true
		}
		if(additionalInfoItem.primaryPhone){
			this.phone = additionalInfoItem.primaryPhone
			isChanged = true
		}
		if(additionalInfoItem.consentAccepted){
			this.consentAccepted = additionalInfoItem.consentAccepted
			this.consentText = additionalInfoItem.consentText
			isChanged = true
		}
		return isChanged
	}

	asParticipant(){
		return new ParticipantItem(this.id, this.name, this.email, this.timeZoneItem, this.role)
	}

	getFirstName(){
		this.setupFirstLastNameFromFullNameIfNeeded()
		return this.firstName
	}

	setName(name){
		this.name = name
		this.setupFirstLastNameFromFullNameIfNeeded()
	}

	setupFirstLastNameFromFullNameIfNeeded(){
		this.name = this.name || ""
		let [firstName, ...lastName] = this.name.split(" ")
		this.firstName = firstName
		this.lastName = (lastName || []).join(" ")
		this.lastName_casefolded = this.caseFoldNormalize(this.lastName)
		this.name_casefolded = this.caseFoldNormalize(this.name)
	}

	getFullName() {
		if(this.firstName && this.lastName){
			return `${this.firstName} ${this.lastName}`
		}
		return this.name
	}

	beforeSaveToDb(options){
		super.beforeSaveToDb(options)
		this.setupFirstLastNameFromFullNameIfNeeded()
		this.name_casefolded = this.caseFoldNormalize(this.name)
		this.lastName_casefolded = this.caseFoldNormalize(this.lastName)
		this.ensureEmails()
		// console.log("ClientProfile: beforeSaveToDb = ", this)
	}

	ensureEmails(){
		try{
			let emailSeparators = [",", ";"]
			if(this.email && stringContainsAny(this.email, emailSeparators)){
				let [primaryEmail, ...secondaryEmails] = this.email.split(emailSeparators)
				this.secondaryEmails.pushUnique(secondaryEmails)
				this.email = primaryEmail
			}
		} catch (e) {
			let _ = logError(e)
		}
	}


	onLoadedFromDb(){
		// console.log("onLoadedFromDb")
		this.setupFirstLastNameFromFullNameIfNeeded()
	}

	migrateSchema(oldVersion, newVersion) {
		super.migrateSchema(oldVersion, newVersion)
	}

	getAge(dateMs){
		let birthday = dateMs || this.dateOfBirth
		if(birthday !== undefined){
			let day = dayjs(birthday)
			console.log("getAge: day = ", day)
			return dayjs().diff(day, 'year')
		}
		return null
	}

	isAgeSet(){
		return this.dateOfBirth !== null
	}

	getTimeZoneId(){
		return this.timeZoneItem ? this.timeZoneItem.timeZoneId : null
	}
	getTimeZoneName(){
		return this.timeZoneItem ? this.timeZoneItem.timeZoneName : null
	}


	getLocalTime(){
		console.log("BaseProfile: this.timeZoneItem = ", this.timeZoneItem, ", this = ", this)
		if(this.timeZoneItem){
			let baseTime = dayjs().tz(this.getTimeZoneId())
			let jan = baseTime.month(0).startOf('month')
			let jul = baseTime.month(6).startOf('month')
			let janUtcOffset = jan.utcOffset()
			let julUtcOffset = jul.utcOffset()
			console.log("jan = ",  jan.format() , ", jul = ", jul.format(), ", janUtcOffset = ", janUtcOffset, ", julUtcOffset = ", julUtcOffset)
			return baseTime
		}
	}

	getLocalTimeFormatted(){
		let localTime = this.getLocalTime()
		if(localTime){
			let diffFormatted = this.timeDifferenceFormatted(localTime)
			diffFormatted ? diffFormatted = ` (${diffFormatted})` : ""
			let localTimeFormatted = localTime.format("hh:mm a")
			return `${localTimeFormatted} local time${diffFormatted}`
		}
	}

	getLocalTimeFullFormatted(){
		let localTime = this.getLocalTime()
		if(localTime){
			let localTimeFormatted = localTime.format("hh:mm a, DD MMM")
			let diffFormatted = this.timeDifferenceFormatted(localTime)
			diffFormatted = diffFormatted ? `\n${diffFormatted}` : ""
			return localTimeFormatted + `${diffFormatted}\n${this.timeZoneItem}`
		}
	}
	getAddressShortFormatted(){
		if(this.primaryAddress){
			return this.primaryAddress.short()
		}
		return ""
	}

	timeDifferenceFormatted(dayjsTime){
		let diff = (dayjsTime.utcOffset() - dayjs().utcOffset())/60
		let diffFormatted = ""
		if(diff){
			let absDiff = Math.abs(diff)
			let plural = absDiff > 1 ? "s" : ""
			diffFormatted = diff > 0 ? `${absDiff} hour${plural} ahead` : `${absDiff} hour${plural} behind`
		}
		return diffFormatted
	}

	canSendInvite(){
		return this.portalInviteStatus === InviteStatus.NOT_SET
	}
	isInvitePending(){
		return this.portalInviteStatus === InviteStatus.INVITE_PENDING
	}
	isInviteAccepted(){
		return this.portalInviteStatus === InviteStatus.INVITE_ACCEPTED
	}

	setInviteStatusPending(){
		this.portalInviteStatus = InviteStatus.INVITE_PENDING
	}
	setInviteStatusAccepted(){
		this.portalInviteStatus = InviteStatus.INVITE_ACCEPTED
	}

}
